import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/stores/auth-store";
import { isAgencyUser, isSuperAdmin } from "@/lib/roles";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";
import { LoginPage } from "@/pages/login";
import { ForgotPasswordPage } from "@/pages/forgot-password";
import { ResetPasswordPage } from "@/pages/reset-password";
import { AcceptInvitationPage } from "@/pages/invitations/accept-invitation";
import { DashboardPage } from "@/pages/dashboard";
import { TenantListPage } from "@/pages/tenants/tenant-list";
import { TenantDetailPage } from "@/pages/tenants/tenant-detail";
import { DeviceListPage } from "@/pages/devices/device-list";
import { DeviceDetailPage } from "@/pages/devices/device-detail";
import { GlobalAlertsPage } from "@/pages/alerts/global-alerts";
import { TenantAlertsPage } from "@/pages/alerts/tenant-alerts";
import { SyncManagementPage } from "@/pages/sync/sync-management";
import { TenantSettingsPage } from "@/pages/tenants/tenant-settings";
import { TenantUsersPage } from "@/pages/tenants/tenant-users";
import { UsersAdminPage } from "@/pages/admin/users-admin";
import { UserDetailPage } from "@/pages/admin/user-detail";
import { AuditLogsPage } from "@/pages/admin/audit-logs";
import { AuthorizeVantaPage } from "@/pages/vanta/authorize-vanta";
import { VantaCallbackPage } from "@/pages/vanta/vanta-callback";
import { VantaIntegrationPage } from "@/pages/vanta/vanta-integration";

function RequireAgency({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  if (!isAgencyUser(user)) {
    const tenantId = user?.tenant_ids?.[0];
    return <Navigate to={tenantId ? `/tenants/${tenantId}` : "/login"} replace />;
  }
  return children;
}

function RequireSuperAdmin({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  if (!isSuperAdmin(user)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ClientRedirect() {
  const { user } = useAuth();
  if (isAgencyUser(user)) return <Navigate to="/" replace />;
  if (user?.tenant_ids && user.tenant_ids.length > 1) return <Navigate to="/tenants" replace />;
  const tenantId = user?.tenant_ids?.[0];
  return <Navigate to={tenantId ? `/tenants/${tenantId}` : "/login"} replace />;
}

export function AppRouter() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return null;

  const loginRedirect = isAgencyUser(user)
    ? "/"
    : user?.tenant_ids && user.tenant_ids.length > 1
      ? "/tenants"
      : `/tenants/${user?.tenant_ids?.[0] ?? ""}`;

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={loginRedirect} replace /> : <LoginPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invitations/accept" element={<AcceptInvitationPage />} />
      <Route path="/vanta/callback" element={<VantaCallbackPage />} />
      <Route path="/vanta-integration" element={<VantaIntegrationPage />} />
      <Route
        element={isAuthenticated ? <AuthenticatedLayout /> : <Navigate to="/login" replace />}
      >
        <Route path="/" element={<RequireAgency><DashboardPage /></RequireAgency>} />
        <Route path="/tenants" element={<TenantListPage />} />
        <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />
        <Route path="/tenants/:tenantId/devices" element={<DeviceListPage />} />
        <Route path="/tenants/:tenantId/devices/:deviceId" element={<DeviceDetailPage />} />
        <Route path="/tenants/:tenantId/users" element={<TenantUsersPage />} />
        <Route path="/tenants/:tenantId/alerts" element={<TenantAlertsPage />} />
        <Route path="/tenants/:tenantId/settings" element={<TenantSettingsPage />} />
        <Route path="/authorize-vanta-integration" element={<AuthorizeVantaPage />} />
        <Route path="/sync" element={<RequireAgency><SyncManagementPage /></RequireAgency>} />
        <Route path="/alerts" element={<RequireAgency><GlobalAlertsPage /></RequireAgency>} />
        <Route path="/admin/users" element={<RequireSuperAdmin><UsersAdminPage /></RequireSuperAdmin>} />
        <Route path="/admin/users/:userId" element={<RequireSuperAdmin><UserDetailPage /></RequireSuperAdmin>} />
        <Route path="/admin/audit-logs" element={<RequireSuperAdmin><AuditLogsPage /></RequireSuperAdmin>} />
      </Route>
      <Route path="*" element={isAuthenticated ? <ClientRedirect /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}
